/**
 * Court / customer pick list with "+ Add new…" right in the dropdown (clerks and supervisors).
 * Before adding, it looks for names that are close to one already on the list and asks
 * "Did you mean …?" so the list doesn't fill up with near-duplicates.
 */
import { useState } from 'react';
import { api, ApiError } from './api.ts';
import { useAuth } from './auth.tsx';
import { ErrorBox, useAction } from './ui.tsx';

type Kind = 'court' | 'customer';
const ADD = '__add__';
const COURT_TYPES: [string, string][] = [
  ['magistrate', 'Magistrate'],
  ['common_pleas', 'Common Pleas'],
  ['family', 'Family Court'],
  ['probate', 'Probate'],
  ['circuit', 'Circuit / General Sessions'],
  ['other', 'Other'],
];
const CUSTOMER_CLASSES: [string, string][] = [
  ['attorney', 'Attorney / law firm'],
  ['magistrate', 'Magistrate'],
  ['court', 'Court'],
  ['government', 'Government agency'],
  ['private', 'Private party'],
];

export function DirectorySelect({
  kind,
  value,
  options,
  onChange,
  onAdded,
  suggestName,
  ariaLabel,
}: {
  kind: Kind;
  value: string;
  options: { id: string; name: string; customer_class?: string }[];
  onChange: (id: string) => void;
  /** Called with the new record's id after it's added (reload the list, then select it). */
  onAdded: (id: string) => void | Promise<void>;
  /** Pre-fill the name (e.g. what the scan read). */
  suggestName?: string | null;
  ariaLabel?: string;
}) {
  const { can } = useAuth();
  const canAdd = can('directory.add') || can('admin.config');
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState(kind === 'court' ? 'magistrate' : 'attorney');
  const [similar, setSimilar] = useState<{ id: string; name: string; similarity: number }[] | null>(null);
  const a = useAction();
  const noun = kind === 'court' ? 'court' : 'customer';

  const open = () => {
    setAdding(true);
    setSimilar(null);
    setName(suggestName ? tidy(suggestName) : '');
  };
  const create = (confirmNotDuplicate: boolean) =>
    a.run(async () => {
      const body = kind === 'court' ? { name: name.trim(), courtType: type, confirmNotDuplicate } : { name: name.trim(), customerClass: type, confirmNotDuplicate };
      try {
        const r = await api.post<{ id: string }>(kind === 'court' ? '/api/courts' : '/api/customers', body);
        setAdding(false);
        setSimilar(null);
        await onAdded(r.id);
      } catch (e) {
        const sim = e instanceof ApiError && e.status === 409 ? ((e as any).details?.similar ?? (e as any).body?.error?.details?.similar) : null;
        if (sim?.length) setSimilar(sim);
        else throw e;
      }
    });

  return (
    <>
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => (e.target.value === ADD ? open() : onChange(e.target.value))}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
            {o.customer_class ? ` (${o.customer_class})` : ''}
          </option>
        ))}
        {canAdd && <option value={ADD}>+ Add a new {noun}…</option>}
      </select>
      {adding && (
        <div className="dir-add" role="group" aria-label={`Add a ${noun}`}>
          <div className="dir-add-row">
            <input
              aria-label={`New ${noun} name`}
              placeholder={kind === 'court' ? 'e.g. Chester County Magistrate Court' : 'e.g. Smith & Jones, P.A.'}
              value={name}
              onChange={(e) => (setName(e.target.value), setSimilar(null))}
              autoFocus
            />
            <select aria-label={kind === 'court' ? 'Court type' : 'Customer type'} value={type} onChange={(e) => setType(e.target.value)}>
              {(kind === 'court' ? COURT_TYPES : CUSTOMER_CLASSES).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          {similar ? (
            <div className="dir-similar" role="alert">
              <strong>Already on the list?</strong> These look close to “{name.trim()}”:
              <ul className="plain">
                {similar.map((s) => (
                  <li key={s.id}>
                    <span className="grow">
                      {s.name} <span className="muted small">· {Math.round(s.similarity * 100)}% alike</span>
                    </span>
                    <button type="button" className="btn sm primary" onClick={() => (onChange(s.id), setAdding(false), setSimilar(null))}>
                      Use this one
                    </button>
                  </li>
                ))}
              </ul>
              <div className="row gap-sm">
                <button type="button" className="btn sm" disabled={a.busy} onClick={() => create(true)}>
                  It’s different — add “{name.trim()}”
                </button>
                <button type="button" className="btn sm ghost" onClick={() => (setAdding(false), setSimilar(null))}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="row gap-sm">
              <button type="button" className="btn sm primary" disabled={a.busy || name.trim().length < 3} onClick={() => create(false)}>
                Add {noun}
              </button>
              <button type="button" className="btn sm ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          )}
          <ErrorBox error={a.error} />
        </div>
      )}
    </>
  );
}

/** "IN THE MAGISTRATE'S COURT" → "Magistrate's Court" as a starting point for the name. */
function tidy(s: string) {
  return s
    .replace(/^\s*in\s+the\s+/i, '')
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .replace(/’S\b|'S\b/g, (m) => m.toLowerCase())
    .trim();
}
